import React, { useMemo } from "react";
import { get } from "lodash";
import { RunnerBI } from "../../../../hooks/useGetRunnerFromBI";
import {
  PerformanceContainer,
  InfoContainer,
  Info,
  SectionContainer
} from "./styled-components";
import { computePercentageOrValue, isTopStat } from "../../computeInfo";
import {
  FirstSectionType,
  SecondSectionType,
  ThirdSectionInfoType
} from "./types";
import { BestPerformanceStats } from "../../../RunnerList/types";

interface PerformanceProps {
  isScratched: boolean;
  runnerInfo: RunnerBI;
  isDriver: boolean;
  isInlineHandicapping?: boolean;
  hasMobileOrder?: boolean;
  bestPerformanceRunners: BestPerformanceStats;
  hasSilk: boolean;
}

const PerformanceInfo = ({
  isScratched,
  runnerInfo,
  isDriver,
  isInlineHandicapping,
  bestPerformanceRunners,
  hasSilk,
  hasMobileOrder = true
}: PerformanceProps) => {
  const showFirstSection = useMemo(() => {
    const {
      jockeyTrainerStarts,
      jockeyTrainerWinPercentage,
      jockeyTrainerTop3Percentage
    } = runnerInfo;

    const firstSectionInfo = {
      jockeyTrainerStarts: {
        value: jockeyTrainerStarts,
        description: isDriver ? "D/T STARTS" : "J/T STARTS"
      },
      jockeyTrainerWinPercentage: {
        value: jockeyTrainerWinPercentage,
        description: "WIN%"
      },
      jockeyTrainerTop3Percentage: {
        value: jockeyTrainerTop3Percentage,
        description: "TOP3%"
      }
    };

    return (
      <SectionContainer isFirstSection data-qa-label="first-section-container">
        {Object.keys(firstSectionInfo).map((key) => {
          const { value, description } =
            firstSectionInfo[key as keyof FirstSectionType];
          const topStat = get(bestPerformanceRunners, key, 0);

          return (
            <InfoContainer
              isFirstSection
              data-qa-label={`first-section-info-${key}`}
              key={key}
            >
              <Info
                isValue
                data-qa-label={`${key}-value`}
                isTopStat={isTopStat(value, topStat)}
              >
                {computePercentageOrValue(value, key)}
              </Info>
              <Info data-qa-label={`${key}-description`} isDescription>
                {description}
              </Info>
            </InfoContainer>
          );
        })}
      </SectionContainer>
    );
  }, [runnerInfo]);

  const showSecondSection = useMemo(() => {
    const { avgSpeed, avgClass, highSpeed } = runnerInfo;

    const secondSectionInfo = {
      avgSpeed: { value: avgSpeed, description: "AVG SPEED" },
      highSpeed: { value: highSpeed, description: "HIGH SPEED" },
      avgClass: { value: avgClass, description: "AVG CLASS" }
    };

    return (
      <SectionContainer
        isSecondSection
        data-qa-label="second-section-container"
      >
        {Object.keys(secondSectionInfo).map((key) => {
          const { value, description } =
            secondSectionInfo[key as keyof SecondSectionType];
          const topStat = get(bestPerformanceRunners, key, 0);

          return (
            <InfoContainer
              data-qa-label={`second-section-info-${key}`}
              key={key}
            >
              <Info
                isValue
                data-qa-label={`${key}-value`}
                isTopStat={isTopStat(value, topStat)}
              >
                {computePercentageOrValue(value, key)}
              </Info>
              <Info data-qa-label={`${key}-description`} isDescription>
                {description}{" "}
              </Info>
            </InfoContainer>
          );
        })}
      </SectionContainer>
    );
  }, [runnerInfo]);

  const showThirdSection = useMemo(() => {
    const { averagePaceEarly, averagePaceMiddle, averagePaceFinish } =
      runnerInfo;

    const thirdSectionInfo = {
      averagePaceEarly: { value: averagePaceEarly, description: "EARLY" },
      averagePaceMiddle: { value: averagePaceMiddle, description: "MIDDLE" },
      averagePaceFinish: { value: averagePaceFinish, description: "FINISH" }
    };

    return (
      <SectionContainer isThirdSection data-qa-label="third-section-container">
        {Object.keys(thirdSectionInfo).map((key) => {
          const { value, description } =
            thirdSectionInfo[key as keyof ThirdSectionInfoType];

          return (
            <InfoContainer
              data-qa-label={`third-section-info-${key}`}
              key={key}
            >
              <Info isValue data-qa-label={`${key}-value`}>
                {computePercentageOrValue(value, key)}
              </Info>
              <Info data-qa-label={`${key}-description`} isDescription>
                {description}
              </Info>
            </InfoContainer>
          );
        })}
      </SectionContainer>
    );
  }, [runnerInfo]);

  return (
    <PerformanceContainer
      data-qa-label="performance-info-container"
      isScratched={isScratched}
      isInlineHandicapping={isInlineHandicapping}
      hasSilk={hasSilk}
      hasMobileOrder={hasMobileOrder}
    >
      {showFirstSection}
      {showSecondSection}
      {showThirdSection}
    </PerformanceContainer>
  );
};

export default PerformanceInfo;
